import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useScrollAwareClick } from '../hooks/useScrollAwareClick';
import {
  Card,
  Button,
  Input,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  Avatar,
  Skeleton,
  Dropdown,
  Badge,
  Empty,
  Pagination,
  Flex,
  Select,
  App,
} from 'antd';
import {
  PlusOutlined,
  FileTextOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  SortAscendingOutlined,
  EyeOutlined,
  SwapOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  CarOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Estimate } from '../types';
import { estimateApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Search } = Input;
const { Text, Title } = Typography;

interface EstimateCardProps {
  estimate: Estimate;
  onView: (estimate: Estimate) => void;
  onEdit: (estimate: Estimate) => void;
  onDelete: (estimate: Estimate, force?: boolean) => void;
  onConvert: (estimate: Estimate) => void;
  onSchedule: (estimate: Estimate) => void;
  isOwner: boolean;
}

const EstimateCard: React.FC<EstimateCardProps> = ({
  estimate,
  onView,
  onEdit,
  onDelete,
  onConvert,
  onSchedule,
  isOwner,
}) => {
  const { getClickHandler, touchHandlers } = useScrollAwareClick();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return '#6b7280';
      case 'sent': return '#3b82f6';
      case 'approved': return '#10b981';
      case 'declined': return '#ef4444';
      case 'converted': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'sent': return 'Sent';
      case 'approved': return 'Approved';
      case 'declined': return 'Declined';
      case 'converted': return 'Converted';
      default: return status;
    }
  };

  const handleMenuClick = (e: any) => {
    e.domEvent?.stopPropagation();

    switch (e.key) {
      case 'view':
        onView(estimate);
        break;
      case 'edit':
        onEdit(estimate);
        break;
      case 'convert':
        onConvert(estimate);
        break;
      case 'schedule':
        onSchedule(estimate);
        break;
      case 'delete':
        onDelete(estimate, false);
        break;
      case 'force-delete':
        onDelete(estimate, true);
        break;
    }
  };

  const actionMenuItems = [
    {
      key: 'view',
      label: 'View Estimate',
      icon: <EyeOutlined />,
    },
    {
      key: 'edit',
      label: 'Edit Estimate',
      icon: <EditOutlined />,
      disabled: estimate.status === 'converted',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'convert',
      label: 'Convert to Work Order',
      icon: <SwapOutlined />,
      disabled: estimate.status !== 'approved',
    },
    {
      key: 'schedule',
      label: 'Schedule Appointment',
      icon: <CalendarOutlined />,
      disabled: estimate.status === 'converted',
    },
    {
      type: 'divider' as const,
    },
    // Regular delete (disabled for converted estimates)
    ...(estimate.status !== 'converted' ? [{
      key: 'delete',
      label: 'Delete Estimate',
      icon: <DeleteOutlined />,
      danger: true,
    }] : []),
    // Force delete (only for owners on converted estimates)
    ...(estimate.status === 'converted' && isOwner ? [{
      key: 'force-delete',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Force Delete
          <ExclamationCircleOutlined />
        </span>
      ),
      icon: <DeleteOutlined />,
      danger: true,
    }] : []),
  ];

  return (
    <Card
      style={{
        borderRadius: 20,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      styles={{ body: { padding: '16px' } }}
      onClick={getClickHandler(() => onView(estimate))}
      {...touchHandlers}
    >
      <div style={{ width: '100%' }}>
        {/* Header with Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                size={48}
                style={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  border: '2px solid white',
                  boxShadow: '0 4px 8px rgba(245, 158, 11, 0.2)'
                }}
                icon={<FileTextOutlined style={{ fontSize: '16px' }} />}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  width: '14px',
                  height: '14px',
                  backgroundColor: getStatusColor(estimate.status),
                  border: '2px solid white',
                  borderRadius: '50%'
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827', marginBottom: '2px', lineHeight: 1.3, wordWrap: 'break-word' }}>
                {estimate.estimate_number}
              </h3>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '0', fontWeight: '400' }}>
                {estimate.customer_notes ? estimate.customer_notes.substring(0, 40) + (estimate.customer_notes.length > 40 ? '...' : '') : 'No description'}
              </p>
            </div>
          </div>
          <Dropdown
            menu={{ items: actionMenuItems, onClick: handleMenuClick }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ color: '#6b7280', flexShrink: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>

        {/* Customer & Vehicle Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '12px 8px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #e0f2fe' }}>
            <div style={{ width: '28px', height: '28px', backgroundColor: '#0ea5e9', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserOutlined style={{ color: 'white', fontSize: '12px' }} />
            </div>
            <div style={{ textAlign: 'center', width: '100%' }}>
              <div style={{ fontSize: '10px', color: '#0369a1', fontWeight: '500', marginBottom: '3px' }}>Customer</div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', lineHeight: '1.3', wordWrap: 'break-word' }} title={estimate.customer?.company_name || [estimate.customer?.first_name, estimate.customer?.last_name].filter(Boolean).join(' ') || '—'}>
                {estimate.customer?.company_name || [estimate.customer?.first_name, estimate.customer?.last_name].filter(Boolean).join(' ') || '—'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '12px 8px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
            <div style={{ width: '28px', height: '28px', backgroundColor: '#f59e0b', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CarOutlined style={{ color: 'white', fontSize: '12px' }} />
            </div>
            <div style={{ textAlign: 'center', width: '100%' }}>
              <div style={{ fontSize: '10px', color: '#92400e', fontWeight: '500', marginBottom: '3px' }}>Vehicle</div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', lineHeight: '1.3', wordWrap: 'break-word' }} title={[estimate.vehicle?.year, estimate.vehicle?.make, estimate.vehicle?.model].filter(Boolean).join(' ') || '—'}>
                {[estimate.vehicle?.year, estimate.vehicle?.make, estimate.vehicle?.model].filter(Boolean).join(' ') || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Status & Amount */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
          <Space size="small">
            <Tag
              style={{
                background: `${getStatusColor(estimate.status)}15`,
                color: getStatusColor(estimate.status),
                border: `1px solid ${getStatusColor(estimate.status)}40`,
                borderRadius: '6px',
                fontWeight: '500',
                fontSize: '11px',
                margin: 0,
                padding: '4px 8px'
              }}
            >
              {getStatusText(estimate.status)}
            </Tag>

            <Badge
              count={estimate.line_items?.length ?? 0}
              size="small"
              style={{ backgroundColor: '#6366f1' }}
            >
              <Text style={{ fontSize: '12px', color: '#6b7280' }}>
                Items
              </Text>
            </Badge>
          </Space>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <DollarOutlined style={{ color: '#10b981', fontSize: '14px' }} />
            <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>
              ${(estimate.total_amount ?? 0).toFixed(2)}
            </Text>
          </div>
        </div>
      </div>
    </Card>
  );
};

const EstimateList: React.FC = () => {
  const { modal, message } = App.useApp();
  const { user } = useAuth();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [filteredEstimates, setFilteredEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'customer' | 'status'>('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const navigate = useNavigate();
  const location = useLocation();

  const isOwner = user?.role === 'owner';

  // Determine route prefix based on current path
  const getRoutePrefix = () => {
    if (location.pathname.startsWith('/owner')) return '/owner';
    if (location.pathname.startsWith('/advisor')) return '/advisor';
    if (location.pathname.startsWith('/technician')) return '/technician';
    if (location.pathname.startsWith('/portal')) return '/portal';
    return '/admin';
  };

  const routePrefix = getRoutePrefix();

  // Debounced fetch: covers initial mount + reacts to search/filter changes.
  // Search keystrokes update searchTerm immediately (responsive input) but the
  // API call only fires ~350ms after typing stops.
  const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    fetchTimeoutRef.current = setTimeout(() => {
      fetchEstimates();
    }, 350);
    return () => clearTimeout(fetchTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    sortEstimates();
  }, [estimates, sortBy]);

  const fetchEstimates = async () => {
    try {
      setLoading(true);
      const response = await estimateApi.getAll({
        search: searchTerm || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter
      });
      setEstimates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching estimates:', error);
      message.error('Failed to fetch estimates');
      setLoading(false);
    }
  };

  const sortEstimates = () => {
    const sorted = [...estimates].sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
        case 'amount':
          return (b.total_amount ?? 0) - (a.total_amount ?? 0);
        case 'customer':
          const nameA = a.customer?.company_name || [a.customer?.first_name, a.customer?.last_name].filter(Boolean).join(' ');
          const nameB = b.customer?.company_name || [b.customer?.first_name, b.customer?.last_name].filter(Boolean).join(' ');
          return nameA.localeCompare(nameB);
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        default:
          return 0;
      }
    });

    setFilteredEstimates(sorted);
    setCurrentPage(1);
  };

  const handleView = (estimate: Estimate) => {
    navigate(`${routePrefix}/estimates/${estimate.id}`);
  };

  const handleEdit = (estimate: Estimate) => {
    navigate(`${routePrefix}/estimates/${estimate.id}/edit`);
  };

  const handleDelete = (estimate: Estimate, force = false) => {
    const title = force ? 'Force Delete Converted Estimate' : 'Delete Estimate';
    const content = force
      ? `⚠️ WARNING: This estimate has been converted to a work order. Force deleting it may cause data inconsistencies. Are you absolutely sure you want to force delete estimate ${estimate.estimate_number}?`
      : `Are you sure you want to delete estimate ${estimate.estimate_number}? This action cannot be undone.`;

    modal.confirm({
      title,
      content,
      okText: force ? 'Force Delete' : 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      icon: force ? <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> : undefined,
      onOk: async () => {
        try {
          await estimateApi.delete(estimate.id!, force);
          message.success(`Estimate ${estimate.estimate_number} deleted successfully`);
          fetchEstimates(); // Refresh the list
        } catch (error: any) {
          console.error('Error deleting estimate:', error);
          console.error('Error response:', error?.response?.data);
          const errorMessage = error?.response?.data?.error || 'Failed to delete estimate';
          message.error(errorMessage);
        }
      },
    });
  };

  const handleConvert = async (estimate: Estimate) => {
    try {
      await estimateApi.convertToWorkOrder(estimate.id!);
      message.success(`Estimate ${estimate.estimate_number} converted to work order successfully`);
      fetchEstimates(); // Refresh the list
    } catch (error) {
      console.error('Error converting estimate:', error);
      message.error('Failed to convert estimate to work order');
    }
  };

  const handleSchedule = (estimate: Estimate) => {
    message.info(`Schedule appointment for ${estimate.estimate_number} - Coming soon`);
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
    { value: 'converted', label: 'Converted' },
  ];

  const sortMenuItems = [
    { key: 'date', label: 'Sort by Date', onClick: () => setSortBy('date') },
    { key: 'amount', label: 'Sort by Amount', onClick: () => setSortBy('amount') },
    { key: 'customer', label: 'Sort by Customer', onClick: () => setSortBy('customer') },
    { key: 'status', label: 'Sort by Status', onClick: () => setSortBy('status') },
  ];

  const paginatedEstimates = filteredEstimates.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return (
      <div style={{ padding: '24px', width: '100%', maxWidth: '100%' }}>
        {/* Header Skeleton */}
        <div style={{ marginBottom: 24 }}>
          <Skeleton.Button active size="large" style={{ width: 200, marginBottom: 12 }} />
          <Skeleton.Input active size="large" block style={{ marginBottom: 16 }} />
        </div>

        {/* Card Skeletons */}
        <Row gutter={[16, 20]}>
          {[...Array(8)].map((_, index) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={index}>
              <Card
                style={{
                  borderRadius: 20,
                  border: '1px solid #e2e8f0',
                }}
              >
                <Skeleton avatar active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', padding: '24px' }}>
      {/* Header */}
      <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: 24 }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
          <Space direction="vertical" size="small">
            <Title level={2} style={{ marginBottom: 0 }}>
              Estimates ({filteredEstimates.length})
            </Title>
            <Text type="secondary">
              Create and manage service estimates for your customers
            </Text>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate(`${routePrefix}/estimates/new`)}
          >
            New Estimate
          </Button>
        </Flex>
        <Flex gap="middle" wrap="wrap">
          <Search
            placeholder="Search estimates by title, customer, vehicle, or number..."
            allowClear
            size="large"
            style={{ flex: 1, minWidth: 200, maxWidth: '100%' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={(value) => setSearchTerm(value)}
          />
          <Space>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              size="large"
              style={{ minWidth: 120 }}
            />
            <Dropdown menu={{ items: sortMenuItems, selectedKeys: [sortBy] }} trigger={['click']}>
              <Button icon={<SortAscendingOutlined />} size="large">
                Sort
              </Button>
            </Dropdown>
          </Space>
        </Flex>
      </Space>

      {/* Estimate Cards */}
      {filteredEstimates.length === 0 ? (
        <Empty
          description="No estimates found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '64px 0' }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`${routePrefix}/estimates/new`)}
          >
            Create Your First Estimate
          </Button>
        </Empty>
      ) : (
        <>
          <Row gutter={[16, 20]} style={{ marginBottom: '24px' }}>
            {paginatedEstimates.map((estimate) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={estimate.id}>
                <EstimateCard
                  estimate={estimate}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onConvert={handleConvert}
                  onSchedule={handleSchedule}
                  isOwner={isOwner}
                />
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {filteredEstimates.length > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '24px' }}>
              <Pagination
                current={currentPage}
                total={filteredEstimates.length}
                pageSize={pageSize}
                onChange={setCurrentPage}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} estimates`
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EstimateList;